import React from 'react';
import {
  Dialog,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { UnknownAction } from '@reduxjs/toolkit';
import DialogTitle from '../atoms/DialogTitle';
import ReducersIndex from 'reduxModules/ducks';
import { RootState } from 'types/redux/state';

/**
 * One config entry per modal flow this dialog handles. Single source of truth for
 * which modal name maps to which copy + dispatch routing — adding a new flow now
 * means one entry here instead of touching modal-name constants, `*Open` booleans,
 * the title ternary, and the body ternary separately.
 *
 * Order matters: when multiple modals are queued (rare — both gap detections fire
 * on the same recalc), `find()` returns the first match. Auto-recommendation flows
 * are listed first because they historically opened first and should win the race.
 *
 * `preConfirm: 'manualOptIn'` flags flows that need to dispatch
 * `setExpansionMicManualOptIn(true)` before the main type-setting dispatch on
 * confirm. The manual-opt-in flag tells the calc to take its standard-placement
 * fallback branch instead of running the gap-aware algorithm.
 */
interface FlowConfig {
  modalName: string;
  isMicFlow: boolean;
  title: string;
  body: string;
  preConfirm?: 'manualOptIn';
}

const FLOWS: readonly FlowConfig[] = [
  {
    modalName: 'expansionMicRecommendation',
    isMicFlow: true,
    title: 'Expansion Microphones recommended',
    body: 'Improve the coverage by adding expansion microphones. Adding expansion microphones with a Parlé bar is only supported by the Devio UCX meeting room hub.',
  },
  {
    modalName: 'expansionMicAdd',
    isMicFlow: true,
    title: 'Add expansion microphones',
    body: 'Adding expansion microphones with a Parlé bar is only supported by the Devio UCX meeting room hub.',
    preConfirm: 'manualOptIn',
  },
  {
    modalName: 'expansionSpeakerRecommendation',
    isMicFlow: false,
    title: 'Expansion Speakers recommended',
    body: 'Improve audio coverage by adding expansion speakers. Adding expansion speakers with a Parlé bar is only supported by the Devio UCX meeting room hub.',
  },
  {
    modalName: 'expansionSpeakerAdd',
    isMicFlow: false,
    title: 'Add expansion speakers',
    body: 'Adding expansion speakers with a Parlé bar is only supported by the Devio UCX meeting room hub.',
  },
];

// Modal-name list derived from the config so the ModalManager filter stays in sync
// automatically when a new flow is added to FLOWS.
export const EXPANSION_RECOMMENDATION_MODALS = FLOWS.map(f => f.modalName);

// The mic-add modal is the one that participates in the click-to-place flow — when
// the user `+`-clicks the canvas before picking an expansion type, the click is parked
// in `ui.pendingExpansionMicLocation` and `expansionMicAdd` opens. Resolved as a
// named constant so the close handler doesn't have to repeat the string.
const MIC_ADD_MODAL_NAME = 'expansionMicAdd';

/**
 * Shared recommendation / add dialog for the bar + expansion mic AND bar + expansion
 * speaker flows. Selects copy + dispatch routing from `FLOWS` based on which modal is
 * currently open. Both flows render identically — only the title, body, and the
 * resulting `setExpansion{Mic,Speaker}Type` action differ.
 *
 * Also handles the click-to-place flow: when `expansionMicAdd` opens with a parked
 * `pendingExpansionMicLocation`, the confirm path uses `placeExpansionMicAtLocation`
 * to drop a single mic at the click coords instead of letting the calc auto-place.
 */
const ExpansionDialog: React.FC = () => {
  const dispatch = useDispatch();
  const modals = useSelector((state: RootState) => state.ui.modals);
  const pendingLocation = useSelector((state: RootState) => state.ui.pendingExpansionMicLocation);

  // Resolve the active flow once from the config table. `find` returns the first match,
  // so FLOWS' order encodes the "auto-recommendation wins" precedence when multiple are
  // queued simultaneously (rare).
  const openModalNames = new Set(modals.filter(m => m.modalOpen).map(m => m.modalName));
  const activeFlow = FLOWS.find(f => openModalNames.has(f.modalName)) ?? null;
  const isOpen = activeFlow !== null;

  const close = (choice: 'none' | 'ceiling') => {
    if (!activeFlow) return;

    dispatch(ReducersIndex.uiReducers.closeModal(activeFlow.modalName) as unknown as UnknownAction);

    // Flow B click-to-place: parked location applies only to the mic add modal. Clear
    // unconditionally on close — confirm uses it below, cancel discards it. Done up
    // front so a stale pendingLocation can't leak into a later dialog open.
    const flowBLocation = activeFlow.modalName === MIC_ADD_MODAL_NAME && pendingLocation ? pendingLocation : null;
    if (pendingLocation) {
      dispatch(ReducersIndex.uiReducers.setPendingExpansionMicLocation(null) as unknown as UnknownAction);
    }

    if (activeFlow.isMicFlow) {
      if (flowBLocation && choice === 'ceiling') {
        // Flow B confirm: place a single mic at the exact click location via the atomic
        // reducer. Skip the manual-opt-in + setExpansionMicType pair because that pair
        // triggers the grid-placement fallback — here we have a real location, no need
        // for a fallback. The atomic reducer also marks customized=true so the next
        // recalc preserves the placement.
        // Argument cast follows the file's existing convention for room-slice reducers
        // (see `setExpansionMicType(choice as any)` below). The slice is mounted via
        // `containerSlice.actions` spread, so RTK can't infer payload types from the
        // untyped `(state, action) =>` signatures — the action creators get `never`
        // payload types. Casting the argument matches the established pattern.
        dispatch(ReducersIndex.container.placeExpansionMicAtLocation({
          type: 'ceiling',
          location: flowBLocation,
        } as any) as unknown as UnknownAction);
        return;
      }
      // Manual "Add" flow (CEILING only) — set the manual-opt-in flag before setExpansionMicType
      // triggers the recalc, so the ceiling calc takes the standard grid-placement fallback
      // branch and produces mics. TABLE expansion does NOT use this flag: its placement is
      // scenario-driven in the middleware (Scenario 1 → full path, Scenario 3 → reduced arc),
      // and setting the flag for table would wrongly flip Tree Frog platform eligibility
      // (treeFrogPorts keys "accepted via recommendation" on !expansionMicManualOptIn).
      // Skipped on the auto path where the gap calc places natively.
      if (activeFlow.preConfirm === 'manualOptIn' && choice === 'ceiling') {
        dispatch(ReducersIndex.container.setExpansionMicManualOptIn(true as any) as unknown as UnknownAction);
      }
      dispatch(ReducersIndex.container.setExpansionMicType(choice as any) as unknown as UnknownAction);
    } else {
      dispatch(ReducersIndex.container.setExpansionSpeakerType(choice as any) as unknown as UnknownAction);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={(_, reason) => { if (reason !== 'backdropClick') close('none'); }}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            border: theme => `0.6px solid ${theme.palette.divider}`,
            borderRadius: '8px',
            minWidth: '400px',
          },
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
        },
      }}
    >
      <DialogTitle onClose={() => close('none')}>
        {activeFlow?.title ?? ''}
      </DialogTitle>
        <Typography variant="body1" sx={{ marginLeft: '16px', marginRight: '16px' }}>
          {activeFlow?.body ?? ''}
        </Typography>
      <DialogActions sx={{ padding: '16px 24px', justifyContent: 'flex-end', gap: '8px' }}>
        <Button
          variant="outlined"
          onClick={() => close('none')}
          sx={{ fontFamily: 'Open Sans', textTransform: 'none', fontWeight: 600, width: '50%', height: '45px' }}
        >
          No thanks
        </Button>
        <Button
          variant="contained"
          onClick={() => close('ceiling')}
          sx={{ fontFamily: 'Open Sans', textTransform: 'none', fontWeight: 600, width: '50%', height: '45px' }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExpansionDialog;
